import React, { useEffect, useState } from "react";
import api, { extractErrorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/i18n";
import { LANGUAGES } from "@/i18n/languages";
import { Send, Eye } from "lucide-react";
import { toast } from "sonner";

const empty = {
  subject_en: "",
  subject_es: "",
  subject_zh: "",
  body_html_en: "<p>Hello {name},</p><p>...</p>",
  body_html_es: "<p>Hola {name},</p><p>...</p>",
  body_html_zh: "",
  profile_keys: [],
  countries: [],
  languages: [],
  require_consent_marketing: true,
  require_email_verified: true,
  min_interest_level: 0,
  completed_questionnaire_id: "",
  not_completed_questionnaire_id: "",
  attach_questionnaire_id: "",
  test_recipient: "",
};

// Always coerce a campaign-style array field to an array so .includes()
// never blows up on legacy / partially-saved campaign records.
const asArray = (v) => (Array.isArray(v) ? v : []);

const AdminCampaigns = () => {
  const { user: me } = useAuth();
  const { t, lang, pickI18n, tProfile, tQuestionnaireTitle } = useLang();
  const isManager = me?.role === "qa_manager";
  const [profiles, setProfiles] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(empty);
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/profiles").then((r) => setProfiles(r.data));
    api.get("/admin/questionnaires").then((r) => setQuestionnaires(r.data.filter((q) => !q.is_internal && q.is_active)));
    api.get("/admin/campaigns").then((r) => setCampaigns(r.data));
  }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildPayload = () => {
    const payload = { ...form };
    payload.profile_keys = asArray(payload.profile_keys);
    payload.countries = asArray(payload.countries);
    payload.languages = asArray(payload.languages);
    if (!payload.profile_keys.length) payload.profile_keys = null;
    if (!payload.countries.length) payload.countries = null;
    if (!payload.languages.length) payload.languages = null;
    if (!payload.completed_questionnaire_id) payload.completed_questionnaire_id = null;
    if (!payload.not_completed_questionnaire_id) payload.not_completed_questionnaire_id = null;
    if (!payload.attach_questionnaire_id) payload.attach_questionnaire_id = null;
    if (!payload.test_recipient) payload.test_recipient = null;
    return payload;
  };

  const doPreview = async () => {
    const { data } = await api.post("/admin/campaigns/preview", buildPayload());
    setPreview(data);
  };

  const doSend = async () => {
    if (isManager && !form.test_recipient) {
      toast.error(t("phase2.campaignsMgrTestOnly"));
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/campaigns/send", buildPayload());
      toast.success(t("admin.campaignSent", { n: data.sent }));
      setForm(empty); setPreview(null);
      api.get("/admin/campaigns").then((r) => setCampaigns(r.data));
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="tsw-overline">{t("admin.title")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-900">{t("admin.campaigns")}</h1>
        {isManager && (
          <p className="text-xs text-amber-700 mt-2 max-w-2xl" data-testid="campaigns-manager-note">
            {t("phase2.campaignsMgrBanner")}
          </p>
        )}
      </div>

      <div className="tsw-card p-6 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <label className="tsw-label">{t("admin.campaignSubject")} (EN)</label>
            <input className="tsw-input" value={form.subject_en} onChange={(e) => update({ subject_en: e.target.value })} data-testid="campaign-subject-en" />
          </div>
          <div>
            <label className="tsw-label">{t("admin.campaignSubject")} (ES)</label>
            <input className="tsw-input" value={form.subject_es} onChange={(e) => update({ subject_es: e.target.value })} data-testid="campaign-subject-es" />
          </div>
          <div>
            <label className="tsw-label">{t("admin.campaignSubject")} (ZH)</label>
            <input className="tsw-input" value={form.subject_zh} onChange={(e) => update({ subject_zh: e.target.value })} data-testid="campaign-subject-zh" />
          </div>
          <div>
            <label className="tsw-label">{t("admin.campaignBody")} (EN)</label>
            <textarea className="tsw-input min-h-[140px] font-mono text-xs" value={form.body_html_en} onChange={(e) => update({ body_html_en: e.target.value })} data-testid="campaign-body-en" />
          </div>
          <div>
            <label className="tsw-label">{t("admin.campaignBody")} (ES)</label>
            <textarea className="tsw-input min-h-[140px] font-mono text-xs" value={form.body_html_es} onChange={(e) => update({ body_html_es: e.target.value })} data-testid="campaign-body-es" />
          </div>
          <div>
            <label className="tsw-label">{t("admin.campaignBody")} (ZH)</label>
            <textarea className="tsw-input min-h-[140px] font-mono text-xs" value={form.body_html_zh} onChange={(e) => update({ body_html_zh: e.target.value })} data-testid="campaign-body-zh" />
          </div>
        </div>

        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-2">{t("phase2.campaignsSegmentation")}</div>
          <div className="flex flex-wrap gap-2">
            {profiles.map((p) => {
              const selectedProfiles = asArray(form.profile_keys);
              const checked = selectedProfiles.includes(p.key);
              return (
                <label key={p.key} className={`tsw-chip cursor-pointer ${checked ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => update({
                    profile_keys: e.target.checked ? [...selectedProfiles, p.key] : selectedProfiles.filter((k) => k !== p.key),
                  })} />
                  {tProfile(p.key, p)}
                </label>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-2 items-center">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{t("phase2.campaignsLanguages")}:</span>
            {LANGUAGES.filter((L) => L.isActive).map((opt) => {
              const selectedLangs = asArray(form.languages);
              const checked = selectedLangs.includes(opt.code);
              return (
                <label key={opt.code} className={`tsw-chip cursor-pointer ${checked ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`} data-testid={`campaign-lang-${opt.code}`}>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => update({
                    languages: e.target.checked ? [...selectedLangs, opt.code] : selectedLangs.filter((l) => l !== opt.code),
                  })} />
                  {opt.nativeName}
                </label>
              );
            })}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.require_consent_marketing} onChange={(e) => update({ require_consent_marketing: e.target.checked })} />
            {t("admin.consentMarketing")} ✓
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={form.require_email_verified} onChange={(e) => update({ require_email_verified: e.target.checked })} />
            {t("admin.verifiedUsers")} ✓
          </label>
          <div>
            <label className="tsw-label">{t("phase2.campaignsMinPrefix")} {t("admin.interest")}</label>
            <input type="number" min={0} max={5} className="tsw-input" value={form.min_interest_level} onChange={(e) => update({ min_interest_level: Number(e.target.value) })} />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="tsw-label">{t("phase2.campaignsCompletedQuestionnaire")}</label>
            <select className="tsw-input" value={form.completed_questionnaire_id} onChange={(e) => update({ completed_questionnaire_id: e.target.value })}>
              <option value="">—</option>
              {questionnaires.map((q) => <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>)}
            </select>
          </div>
          <div>
            <label className="tsw-label">{t("phase2.campaignsNotCompletedQuestionnaire")}</label>
            <select className="tsw-input" value={form.not_completed_questionnaire_id} onChange={(e) => update({ not_completed_questionnaire_id: e.target.value })}>
              <option value="">—</option>
              {questionnaires.map((q) => <option key={q.id} value={q.id}>{tQuestionnaireTitle(q)}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="tsw-label">{t("admin.campaignTestRecipient")}</label>
          <input type="email" className="tsw-input" placeholder="test@example.com" value={form.test_recipient} onChange={(e) => update({ test_recipient: e.target.value })} data-testid="campaign-test-recipient" />
        </div>

        <div className="flex gap-2 pt-2 border-t border-slate-100">
          <button onClick={doPreview} className="tsw-btn-secondary" data-testid="campaign-preview"><Eye className="w-4 h-4" /> {t("admin.previewCampaign")}</button>
          <button onClick={doSend} disabled={sending || (isManager && !form.test_recipient)} className="tsw-btn-primary" data-testid="campaign-send">
            <Send className="w-4 h-4" /> {sending ? t("common.sending") : t("admin.sendCampaign")}
          </button>
        </div>

        {preview && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm" data-testid="campaign-preview-result">
            <div className="font-semibold text-slate-900">{t("phase2.campaignsAudience")}: {preview.matches}</div>
            <ul className="mt-2 text-slate-600 space-y-0.5">
              {preview.sample.map((s) => <li key={s.email}>· {s.email} ({s.profile_key}, {s.country || "—"})</li>)}
            </ul>
          </div>
        )}
      </div>

      <div className="tsw-card p-5">
        <div className="font-semibold text-slate-900 mb-3">{t("phase2.campaignsRecent")}</div>
        <ul className="space-y-2 text-sm">
          {campaigns.map((c) => {
            const subject = pickI18n(c, "subject") || c.subject_en || c.subject_es || c.subject_zh || t("phase2.campaignsUntitled");
            const created = c.created_at ? new Date(c.created_at).toLocaleString() : "—";
            const count = typeof c.recipient_count === "number" ? c.recipient_count : 0;
            return (
              <li key={c.id} className="flex items-center justify-between border-b border-slate-100 pb-2 last:border-0" data-testid={`campaign-row-${c.id}`}>
                <span className="text-slate-700">{subject}</span>
                <span className="text-slate-500 text-xs">{created} · {count} {c.test ? t("phase2.campaignsTest") : t("phase2.campaignsSent")}</span>
              </li>
            );
          })}
          {campaigns.length === 0 && <li className="text-slate-400">—</li>}
        </ul>
      </div>
    </div>
  );
};

export default AdminCampaigns;
